import { useOutletContext } from '@remix-run/react';
import { type DehydratedState } from '@tanstack/react-query';

import { ArticleTypes } from '@cx-modules/more-articles';

import Onboarding, { onboardingVariants } from '~/components/Onboarding';
import ParentLinkHeader from '~/components/ParentLinkHeader';
import { OffersWidget } from '~/components/Widgets/Offers/Offers';
import {
  MatchesCoupon,
  MoreArticles,
  OutrightsCoupon,
  TextBlock } from
'~/modules';
import {
  featureFlags,
  useFeatureFlag } from
'~/providers/feature-flag-provider';
import { type StateCode } from '~/services/actions/state-actions';

import styles from '../common/card.module.css';

import { type ActiveCardRouteData } from './loader';

export { meta } from './meta';

export default function CardIndex() {
  const {
    DISABLE_OFFER_WIDGETS,
    baseUrl,
    bettypeIds,
    cardId,
    componentState: {
      matchesCoupon,
      moreArticlesInsights,
      moreArticlesPicks,
      outrightsCoupon,
      textBlock
    },
    deviceType,
    eventId,
    geoLocationInfo,
    level,
    matchesEventName,
    matchesEventUrl,
    oddsFormat,
    outrightBetTypeIds,
    outrightEventIds,
    parentLinkHeader,
    repub,
    subeventLimit,
    topicForArticles
  } = useOutletContext<ActiveCardRouteData>();

  const shouldRenderOnboardingAsMyOcLoginCta = useFeatureFlag(
    featureFlags.RENDER_ONBOARDING_AS_MYOC_LOGIN_CTA
  );

  const renderMatchesCoupon = () => {
    if (!matchesCoupon) return null;
    return (
      <div>
				<div className={styles.heading}>Matches</div>
				<MatchesCoupon
          baseUrl={baseUrl}
          bettypeIds={bettypeIds}
          countryCode={geoLocationInfo.countryCode}
          dataApiEndpoint="/api"
          dehydratedState={matchesCoupon}
          deviceType={deviceType}
          eventId={eventId}
          eventName={matchesEventName}
          eventUrl={matchesEventUrl}
          key={eventId}
          oddsFormat={oddsFormat}
          repub={repub}
          showEventCta={false}
          subdivision={geoLocationInfo.subdivisionCode}
          subeventLimit={subeventLimit} />

			</div>);

  };

  const renderMoreArticles = (
  type: ArticleTypes,
  dehydratedState?: DehydratedState) =>
  {
    if (!dehydratedState) return null;

    const isTips = type === ArticleTypes.Tips;
    const path = isTips ? 'picks-parlays' : 'insight';

    return (
      <MoreArticles
        countryCode={geoLocationInfo.countryCode}
        dataApiEndpoint="/api"
        dehydratedState={dehydratedState}
        limit={5}
        moreArticlesPath={`/${repub.toLowerCase()}/${path}`}
        moreArticlesText={isTips ? 'More Picks' : 'More Insight'}
        offset={0}
        repub={repub}
        subdivision={geoLocationInfo.subdivisionCode}
        title={isTips ? 'Picks' : 'Insight'}
        topics={topicForArticles}
        type={type} />);


  };

  const renderOutrightsCoupon = () => {
    if (!outrightsCoupon) return null;
    return (
      <OutrightsCoupon
        bettypeIds={outrightBetTypeIds}
        countryCode={geoLocationInfo.countryCode}
        dataApiEndpoint="/api"
        dehydratedState={outrightsCoupon}
        deviceType={deviceType}
        eventIds={outrightEventIds}
        oddsFormat={oddsFormat}
        repub={repub}
        subdivision={geoLocationInfo.subdivisionCode} />);


  };

  const renderTextBlock = () => {
    if (!textBlock) return null;
    return (
      <TextBlock
        dataApiEndpoint="/api"
        dehydratedState={textBlock}
        id={cardId}
        level={level}
        repub={repub} />);


  };

  return (
    <div className={styles.card}>
			<ParentLinkHeader
        breadcrumb={{
          name: parentLinkHeader.breadcrumb.name,
          url: parentLinkHeader.breadcrumb.url
        }}
        title={parentLinkHeader.title} />

			<OffersWidget
        DISABLE_OFFER_WIDGETS={DISABLE_OFFER_WIDGETS}
        className="card"
        geotargeting=""
        inheritFontFamily=""
        theme="ocus"
        variation="horizontal-slim"
        vcId="4yyFzILMXoWgjWP0AKAaoV" />

			{/* {renderFeaturedMatchesWrapper()} */}

			<div className={styles.container}>
				{/* Primary */}
				<div className={styles.primary}>
					{renderMatchesCoupon()}
					{renderOutrightsCoupon()}
				</div>

				{/* Secondary */}
				<div className={styles.secondary}>
					<div className={styles.signUpWrapper}>
						<Onboarding
              baseUrl={'/us/'}
              contentServer="https://static.oddschecker.com/content/international/"
              state={geoLocationInfo.subdivisionCode as StateCode}
              variant={
              shouldRenderOnboardingAsMyOcLoginCta ?
              onboardingVariants.USER_SIGNUP :
              onboardingVariants.NEWSLETTER_SIGNUP
              } />

					</div>
					{renderMoreArticles(ArticleTypes.Tips, moreArticlesPicks)}
					{renderMoreArticles(ArticleTypes.Insights, moreArticlesInsights)}
				</div>
				{renderTextBlock()}
			</div>
		</div>);

}